import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface ExecutionDetail {
  readonly reportExecutionId: string;
  readonly reportName: string;
  readonly status: string;
  readonly rowCount: number;
  readonly outputFormat: string;
  readonly requestedBy: string;
  readonly startedAt: string;
  readonly completedAt: string | null;
  readonly errorMessage: string | null;
}

/** L16.03 — Report run detail (`/reports/runs/:runId`). Read-only view of a
 *  single report execution. */
export function M16ReportRunDetailPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { runId } = useParams<{ runId: string }>();
  const { token } = useAuth();
  const [run, setRun] = useState<ExecutionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<ExecutionDetail>(`/v1/m16/reports/executions/${runId}`, { token })
      .then((r) => { if (alive) setRun(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [runId, token]);

  if (error) return <p role="alert">{error}</p>;
  if (!run) return <p>{t('m16.runDetail.loading')}</p>;
  return (
    <main aria-label="report-run-detail">
      <h1>{t('m16.runDetail.title')}</h1>
      <dl>
        <dt>{t('m16.runDetail.labelReport')}</dt><dd>{run.reportName}</dd>
        <dt>{t('m16.runDetail.labelStatus')}</dt><dd>{run.status}</dd>
        <dt>{t('m16.runDetail.labelRows')}</dt><dd>{run.rowCount}</dd>
        <dt>{t('m16.runDetail.labelFormat')}</dt><dd>{run.outputFormat}</dd>
        <dt>{t('m16.runDetail.labelRequestedBy')}</dt><dd>{run.requestedBy}</dd>
        <dt>{t('m16.runDetail.labelStarted')}</dt><dd>{at(run.startedAt)}</dd>
        <dt>{t('m16.runDetail.labelCompleted')}</dt><dd>{at(run.completedAt)}</dd>
        {run.errorMessage ? <><dt>{t('m16.runDetail.labelError')}</dt><dd role="alert">{run.errorMessage}</dd></> : null}
      </dl>
    </main>
  );
}
